/**
 * Changement de mot de passe par son titulaire : ecran impose apres un mot de
 * passe provisoire, carte « Mot de passe » du profil. Regles affichees et
 * validation viennent des schemas partages : l'interface ne peut pas annoncer
 * une regle que l'API n'applique pas.
 */
import { useId, useRef, useState } from 'react';
import type { FormEvent, RefObject } from 'react';
import { Circle, CircleCheck } from 'lucide-react';
import { changerMotDePasseSchema, motDePasseSchema } from '@leads/shared';
import { erreurDeChamp, messageErreur } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Bandeau, Bouton, Champ, signalerErreursDeChamps } from './ui';

/** Longueur minimale, lue dans le schema partage plutot que recopiee. */
export const LONGUEUR_MIN_MOT_DE_PASSE = motDePasseSchema.minLength ?? 0;

type ChampMotDePasse = 'motDePasseActuel' | 'nouveauMotDePasse' | 'confirmation';
type Erreurs = Partial<Record<ChampMotDePasse, string>>;

/** Ordre du formulaire : apres un refus, le premier champ en erreur prend le focus. */
const ORDRE_CHAMPS: readonly ChampMotDePasse[] = ['motDePasseActuel', 'nouveauMotDePasse', 'confirmation'];

export interface RegleMotDePasse {
  libelle: string;
  respectee: boolean;
}

/** Regles montrees sous le nouveau mot de passe, cochees au fil de la frappe. */
export function reglesMotDePasse(
  libelleActuel: string,
  actuel: string,
  nouveau: string,
  confirmation: string,
): RegleMotDePasse[] {
  return [
    {
      libelle: `Au moins ${LONGUEUR_MIN_MOT_DE_PASSE} caractères`,
      respectee: nouveau.length >= LONGUEUR_MIN_MOT_DE_PASSE,
    },
    { libelle: `Différent du ${libelleActuel.toLowerCase()}`, respectee: nouveau !== '' && nouveau !== actuel },
    { libelle: "Saisi deux fois à l'identique", respectee: nouveau !== '' && nouveau === confirmation },
  ];
}

function validerLocalement(actuel: string, nouveau: string, confirmation: string): Erreurs {
  const erreurs: Erreurs = {};
  const resultat = changerMotDePasseSchema.safeParse({ motDePasseActuel: actuel, nouveauMotDePasse: nouveau });
  if (!resultat.success) {
    const parChamp = resultat.error.flatten().fieldErrors;
    erreurs.motDePasseActuel = parChamp.motDePasseActuel?.[0];
    erreurs.nouveauMotDePasse = parChamp.nouveauMotDePasse?.[0];
  }
  if (confirmation !== nouveau) erreurs.confirmation = 'Les deux saisies ne correspondent pas.';
  return erreurs;
}

export function FormulaireMotDePasse({
  libelleActuel,
  libelleEnvoi,
  onSucces,
  identifiant,
  pleineLargeur = false,
}: {
  /** « Mot de passe provisoire » sur l'ecran impose, « Mot de passe actuel » sur le profil. */
  libelleActuel: string;
  libelleEnvoi: string;
  /** Appele une fois le nouveau jeton enregistre ; les champs sont alors vides. */
  onSucces: () => void;
  /**
   * Adresse du compte, dans un champ masque : un gestionnaire de mots de
   * passe rattache ainsi le nouveau mot de passe au bon compte.
   */
  identifiant?: string;
  pleineLargeur?: boolean;
}) {
  const { changerMotDePasse } = useAuth();
  const idRegles = useId();
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreurs, setErreurs] = useState<Erreurs>({});
  const [erreurGenerale, setErreurGenerale] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const refs: Record<ChampMotDePasse, RefObject<HTMLInputElement | null>> = {
    motDePasseActuel: useRef<HTMLInputElement>(null),
    nouveauMotDePasse: useRef<HTMLInputElement>(null),
    confirmation: useRef<HTMLInputElement>(null),
  };

  const signaler = (suivantes: Erreurs): void =>
    signalerErreursDeChamps(
      () => setErreurs(suivantes),
      ORDRE_CHAMPS.map((nom) => [suivantes[nom], refs[nom]] as const),
    );

  const soumettre = async (evenement: FormEvent<HTMLFormElement>): Promise<void> => {
    evenement.preventDefault();
    setErreurGenerale(null);
    const locales = validerLocalement(actuel, nouveau, confirmation);
    if (Object.values(locales).some(Boolean)) {
      signaler(locales);
      return;
    }
    setErreurs({});

    setEnvoi(true);
    try {
      await changerMotDePasse({ motDePasseActuel: actuel, nouveauMotDePasse: nouveau });
      setActuel('');
      setNouveau('');
      setConfirmation('');
      onSucces();
    } catch (cause) {
      // 400 VALIDATION : l'API designe le champ fautif (mot de passe actuel errone...).
      const surChamps: Erreurs = {
        motDePasseActuel: erreurDeChamp(cause, 'motDePasseActuel'),
        nouveauMotDePasse: erreurDeChamp(cause, 'nouveauMotDePasse'),
      };
      if (surChamps.motDePasseActuel || surChamps.nouveauMotDePasse) signaler(surChamps);
      else setErreurGenerale(messageErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  const champ = (
    libelle: string,
    nomChamp: ChampMotDePasse,
    valeur: string,
    changer: (valeur: string) => void,
    autoComplete: 'current-password' | 'new-password',
    decritAussi?: string,
  ) => (
    <Champ libelle={libelle} erreur={erreurs[nomChamp]}>
      {(id, decritPar) => (
        <input
          ref={refs[nomChamp]}
          id={id}
          name={nomChamp}
          type="password"
          autoComplete={autoComplete}
          className="champ"
          value={valeur}
          onChange={(evenement) => changer(evenement.target.value)}
          aria-invalid={erreurs[nomChamp] ? true : undefined}
          aria-describedby={[decritPar, decritAussi].filter(Boolean).join(' ') || undefined}
        />
      )}
    </Champ>
  );

  return (
    <form onSubmit={(evenement) => void soumettre(evenement)} noValidate className="space-y-4">
      {identifiant !== undefined && (
        <input type="email" name="username" autoComplete="username" value={identifiant} readOnly hidden />
      )}
      {champ(libelleActuel, 'motDePasseActuel', actuel, setActuel, 'current-password')}
      {champ('Nouveau mot de passe', 'nouveauMotDePasse', nouveau, setNouveau, 'new-password', idRegles)}

      <ul id={idRegles} aria-label="Règles du mot de passe" className="space-y-1">
        {reglesMotDePasse(libelleActuel, actuel, nouveau, confirmation).map((regle) => (
          <li
            key={regle.libelle}
            className={`flex items-center gap-2 text-sm ${regle.respectee ? 'text-succes-700' : 'text-encre-discrete'}`}
          >
            {regle.respectee ? <CircleCheck aria-hidden="true" size={14} /> : <Circle aria-hidden="true" size={14} />}
            {regle.libelle}
            <span className="sr-only">{regle.respectee ? ' : respectée' : ' : à respecter'}</span>
          </li>
        ))}
      </ul>

      {champ('Confirmer le nouveau mot de passe', 'confirmation', confirmation, setConfirmation, 'new-password')}

      {erreurGenerale && (
        <Bandeau ton="danger" role="alert" titre="Mot de passe inchangé">
          {erreurGenerale}
        </Bandeau>
      )}

      <Bouton
        type="submit"
        variante="primaire"
        taille={pleineLargeur ? 'lg' : 'md'}
        className={pleineLargeur ? 'w-full' : ''}
        chargement={envoi}
      >
        {libelleEnvoi}
      </Bouton>
    </form>
  );
}
